import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Container,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  FormHelperText,
} from "@mui/material";
import { etiklik, location, department } from "../../helper/data";
import useFeedbackCall from "../../hooks/useFeedbackCall";
import { validateContactFields } from "../../helper/contactValidation";

// Etik bildirimlerde anonimlik korunur; yalnızca doldurulan iletişim alanları doğrulanır.
const CONTACT_RULES = {
  name: { type: "name", label: "İsim", required: false },
  surname: { type: "name", label: "Soyisim", required: false },
  phone: { type: "phone", label: "Telefon", required: false },
  email: { type: "email", label: "E-posta", required: false },
}

const Etiklik = ({ loading }) => {
  const now = new Date();

  // Gün, Ay ve Yıl için değerleri al
  const day = String(now.getDate()).padStart(2, "0"); // Günü 2 basamaklı yap
  const month = String(now.getMonth() + 1).padStart(2, "0"); // Ayı 2 basamaklı yap (0'dan başladığı için +1 ekliyoruz)
  const year = now.getFullYear();

  // Saat, Dakika ve Saniye için değerleri al
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");

  // Düzenlenmiş tarih ve saati birleştir
  const formattedDate = `${day}-${month}-${year} ${hours}:${minutes}`;

  const { postFireData } = useFeedbackCall();

  const [bonnaUser, setBonnaUser] = useState(false);

  const [selectedData, setSelectedData] = useState([]);

  const [info, setInfo] = useState({
    name: "",
    surname: "",
    phone: "",
    email: "",
    topic: "Etiklik",
    title: "",
    subTitle: "",
    document: "",
    location: "",
    gorev: "",
    birim: "",
    actionType: "",
    actionResult: "",
    bonnaUser: bonnaUser ? "Bonna Çalışanı" : "Misafir",
    datetime: formattedDate,
  });
  const [errors, setErrors] = useState({});

  const handleTitleChange = (event) => {
    const newTitle = event.target.value;
    setInfo((prev) => ({ ...prev, title: newTitle, subTitle: "" })); // subTitle sıfırlanır
  };

  const handleSubTitleChange = (event) => {
    setInfo((prev) => ({ ...prev, subTitle: event.target.value }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInfo((prevInfo) => ({ ...prevInfo, [name]: value }));

    if (CONTACT_RULES[name]) {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: undefined }));
    }
  };

  const handleContactBlur = (e) => {
    const { name } = e.target;
    if (!CONTACT_RULES[name]) return;

    const validation = validateContactFields(info, { [name]: CONTACT_RULES[name] });
    setErrors((prevErrors) => ({ ...prevErrors, [name]: validation.errors[name] }));
  };

  const handleChangeFile = (e) => {
    const file = e.target.files[0]; // sadece ilk dosyayı al
    if (file) {
      setInfo((prev) => ({
        ...prev,
        document: file,
      }));
    }
  };

  const selectedCategory = etiklik?.find((cat) => cat?.title === info?.title);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validation = validateContactFields(info, CONTACT_RULES);
    if (!validation.isValid) {
      setErrors(validation.errors);
      e.currentTarget.elements.namedItem(Object.keys(validation.errors)[0])?.focus();
      return;
    }

    setErrors({});
    await postFireData("etiklik", validation.values);
    setInfo({
      name: "",
      surname: "",
      phone: "",
      email: "",
      topic: "Etiklik",
      title: "",
      subTitle: "",
      document: "",
      location: "",
      gorev: "",
      birim: "",
      actionType: "",
      actionResult: "",
      bonnaUser: bonnaUser ? "Bonna Çalışanı" : "Misafir",
      datetime: formattedDate,
    });
  };

  const handleIsCheck = (e) => {
    const { checked } = e.target;
    checked ? setBonnaUser(true) : setBonnaUser(false);
  };

  useEffect(() => {
    // Seçili kategoriyi hesapla (title benzersiz varsayımıyla)
    const selectedCat = etiklik?.find((c) => c?.title === info?.title);
    setSelectedData(selectedCat);
  }, [info.title]);

  return (
    <div>
      <Typography
        align="center"
        color="#FFB534"
        p={3}
        fontWeight={700}
        fontSize={22}
      >
        Etik
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          alignItems: "flex-start",
          p: 2,
        }}
      >
        <Typography variant="subtitle2" align="left" color={"error"}>
          Etik hattına yapılan tüm ihbarlar, en üst düzey gizlilik anlayışıyla
          ele alınır. Bildirimlere erişim yalnızca gizlilik esasları
          doğrultusunda yetkilendirilmiş kişilerle sınırlıdır; başka hiçbir
          çalışan, yönetici ya da üçüncü şahıs bu bilgilere ulaşamaz.
        </Typography>
        <p></p>
        <Typography variant="subtitle2" align="left" color={"error"}>
          İhbarcı çalışan; işten çıkarma, baskı veya terfi engelleme gibi
          misilleme eylemlerine karşı tam anlamıyla güvence altındadır.
        </Typography>
        <p></p>
        <Typography variant="subtitle2" align="left" color={"error"}>
          Çalışanlar, kimliklerini açıklamak zorunda kalmadan anonim ihbarda
          bulunabilir.
        </Typography>
        <p></p>
        <Typography variant="subtitle2" align="left" color={"error"}>
          Şirket, ihbarcıyı korumayı kurumsal bir sorumluluk olarak kabul eder
          ve misilleme girişimlerini disiplin süreciyle karşılık görür.
        </Typography>
        <p></p>
        <Typography variant="subtitle2" align="left" color={"error"}>
          Kötü niyetle veya iftira amacıyla yapıldığı değerlendirilen ihbarlar
          da aynı titizlikle incelenir.
        </Typography>
      </Box>

      <Container
        maxWidth="sm"
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 3,
          p: 5,
        }}
        component={"form"}
        onSubmit={handleSubmit}
      >
        <FormControl fullWidth>
          <InputLabel id="title-select-label">Etik Kategori</InputLabel>
          <Select
            labelId="title-select-label"
            value={info.title}
            label="Etik Kategori"
            onChange={handleTitleChange}
            required
          >
            {etiklik?.map((cat) => {
              return (
                <MenuItem key={cat?.title} value={cat?.title}>
                  {cat?.title}
                </MenuItem>
              );
            })}
          </Select>
          <FormHelperText>{selectedData?.subTitle || ""}</FormHelperText>
        </FormControl>

        <FormControl fullWidth disabled={!info.title}>
          <InputLabel id="subtitle-select-label">Alt Başlık</InputLabel>
          <Select
            labelId="subtitle-select-label"
            value={info.subTitle}
            label="Alt Başlık"
            onChange={handleSubTitleChange}
            required
          >
            {selectedCategory?.items.map((item, idx) => (
              <MenuItem key={idx} value={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 3,
          }}
        >
          <TextField
            fullWidth
            label="İsim"
            name="name"
            id="name"
            type="text"
            value={info.name}
            // inputProps={{
            //   maxLength: 50
            // }}
            onChange={handleChange}
            onBlur={handleContactBlur}
            error={!!errors.name}
            helperText={errors.name || "Opsiyonel"}
          />
          <TextField
            fullWidth
            label="Soyisim"
            name="surname"
            id="surname"
            type="text"
            value={info.surname}
            // inputProps={{
            //   maxLength: 50
            // }}
            onChange={handleChange}
            onBlur={handleContactBlur}
            error={!!errors.surname}
            helperText={errors.surname || "Opsiyonel"}
          />
          <TextField
            fullWidth
            label="Mail Adres"
            name="email"
            id="email"
            type="email"
            value={info.email}
            // inputProps={{
            //   maxLength: 50
            // }}
            onChange={handleChange}
            onBlur={handleContactBlur}
            error={!!errors.email}
            helperText={errors.email || "Opsiyonel"}
          />
          <TextField
            fullWidth
            label="Telefon"
            name="phone"
            id="phone"
            type="tel"
            value={info.phone}
            // inputProps={{
            //   maxLength: 50
            // }}
            onChange={handleChange}
            onBlur={handleContactBlur}
            error={!!errors.phone}
            helperText={errors.phone || "Opsiyonel"}
          />
          <FormControl fullWidth>
            <InputLabel id="location">Lokasyon</InputLabel>
            <Select
              required
              labelId="location"
              name="location"
              id="location"
              label="location"
              value={info.location}
              onChange={handleChange}
            >
              {location?.map((item, index) => (
                <MenuItem key={index} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box width={"100%"}>
            <FormControlLabel
              control={<Checkbox onChange={handleIsCheck} name="gilad" />}
              label="Bonna Çalışanı mısın ?"
            />
          </Box>

          {/* bonna çalışanı ise burayı göster */}
          {bonnaUser && (
            <Box
              width={"100%"}
              sx={{
                display: "flex",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: 3,
                border: "1px solid #7487daed",
                borderRadius: 3,
                p: 2,
              }}
            >
              <TextField
                fullWidth
                label="Görev"
                name="gorev"
                id="gorev"
                type="text"
                value={info.gorev}
                // inputProps={{
                //   maxLength: 35
                // }}
                onChange={handleChange}
                helperText="Opsiyonel"
              />
              <FormControl fullWidth>
                <InputLabel id="birim">Departman</InputLabel>
                <Select
                  required
                  labelId="birim"
                  name="birim"
                  id="birim"
                  label="birim"
                  value={info.birim}
                  onChange={handleChange}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        overflow: "auto",
                      },
                    },
                  }}
                >
                  {department?.map((item, index) => (
                    <MenuItem key={index} value={item.name}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Opsiyonel</FormHelperText>
              </FormControl>
            </Box>
          )}

          <TextField
            fullWidth
            required
            multiline
            rows={4}
            label="Açıklama"
            name="detail"
            id="detail"
            type="text"
            value={info.detail}
            // inputProps={{
            //   maxLength: 50
            // }}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            name="document"
            id="document"
            type="file"
            // inputProps={{
            //   maxLength: 50
            // }}
            onChange={handleChangeFile}
          />
        </Box>

        {loading ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          <Button
            disabled={loading}
            variant="contained"
            fullWidth
            type="submit"
          >
            Kayıt
          </Button>
        )}
      </Container>
    </div>
  );
};

export default Etiklik;
